import { createSelector } from "reselect";

import OrganizationFeature from "@mapmycustomers/shared/enum/OrganizationFeature";
import { EntityType, EntityTypesSupportedByMapsPage } from "@mapmycustomers/shared/types/entity";
import PinLegend, {
  ColorPinLegend,
  ShapePinLegend,
} from "@mapmycustomers/shared/types/map/PinLegend";

import {
  getFeatures,
  getDefaultColorKeys as getOrganizationDefaultColorKeys,
  getDefaultShapeKeys as getOrganizationDefaultShapeKeys,
  getOrganizationId,
  isCurrentUserOwner,
} from "@app/store/iam";
import { RootState } from "@app/store/rootReducer";
import { getDefaultColorPinLegend, getDefaultShapePinLegend } from "@app/util/pinLegend";

const pinLegendsState = (state: RootState) => state.pinLegends;

// DO NOT USE this selector. It's only used in one place and that's for a very good reason.
// In all other places please use getAllColorLegends
export const getAllColorLegendsRaw = createSelector(
  pinLegendsState,
  ({ colorLegends }) => colorLegends
);

export const getAllColorLegends = createSelector(
  pinLegendsState,
  getFeatures,
  ({ colorLegends }, features) => {
    const disallowColor = features?.[OrganizationFeature.DISALLOW_COLOR]?.enabled;
    return disallowColor
      ? colorLegends.filter(
          (legend: PinLegend) => legend.fieldName !== "color" || legend.entity === EntityType.DEAL
        )
      : colorLegends;
  }
);
export const getColorLegends = createSelector(
  [getAllColorLegends, (_, entityType: EntityTypesSupportedByMapsPage) => entityType],
  (colorLegends: ColorPinLegend[], entityType) =>
    colorLegends.filter(({ entity }) => entity === entityType)
);

export const getAllShapeLegends = createSelector(
  pinLegendsState,
  ({ shapeLegends }) => shapeLegends
);
export const getShapeLegends = createSelector(
  [getAllShapeLegends, (_, entityType: EntityTypesSupportedByMapsPage) => entityType],
  (shapeLegends: ShapePinLegend[], entityType) =>
    shapeLegends.filter(({ entity }) => entity === entityType)
);

// The backend computes legend bucket counts across the whole organization, so a rep reading
// "Closed Lost (11)" learns how many records exist company-wide, including ones they cannot
// access. These organizations asked us to keep those totals away from everyone but their owner.
// Remove this once the counts are scoped per user server-side.
const HIDDEN_LEGEND_COUNTS_ORGANIZATION_IDS: ReadonlyArray<number> = [30147, 33917];

export const arePinLegendCountsHidden = createSelector(
  getOrganizationId,
  isCurrentUserOwner,
  (organizationId, isOwner) =>
    organizationId !== undefined &&
    HIDDEN_LEGEND_COUNTS_ORGANIZATION_IDS.includes(organizationId) &&
    !isOwner
);

export const isDeleting = createSelector(pinLegendsState, ({ deleting }) => deleting);

export const isRefreshing = createSelector(pinLegendsState, ({ refreshing }) => refreshing);

export const getDefaultColorKeys = createSelector(
  getAllColorLegends,
  getOrganizationDefaultColorKeys,
  (colorLegends, defaultColorKeys) =>
    (
      [EntityType.COMPANY, EntityType.PERSON, EntityType.DEAL] as EntityTypesSupportedByMapsPage[]
    ).reduce(
      (result, entityType) => {
        let colorKey = defaultColorKeys?.[entityType];
        if (!colorKey || !colorLegends.some(({ id }) => id === colorKey)) {
          // if default pin legend not set or if it's not valid anymore, we fallback to a default one:
          colorKey = getDefaultColorPinLegend(colorLegends, entityType)?.id;
        }
        return { ...result, [entityType]: colorKey };
      },
      {} as Partial<Record<EntityTypesSupportedByMapsPage, PinLegend["id"]>>
    )
);

export const getDefaultShapeKeys = createSelector(
  getAllShapeLegends,
  getOrganizationDefaultShapeKeys,
  (shapeLegends, defaultShapeKeys) =>
    (
      [EntityType.COMPANY, EntityType.PERSON, EntityType.DEAL] as EntityTypesSupportedByMapsPage[]
    ).reduce(
      (result, entityType) => {
        let shapeKey = defaultShapeKeys?.[entityType];
        if (!shapeKey || !shapeLegends.some(({ id }) => id === shapeKey)) {
          // if default pin legend not set or if it's not valid anymore, we fallback to a default one:
          shapeKey = getDefaultShapePinLegend(shapeLegends, entityType)?.id;
        }
        return { ...result, [entityType]: shapeKey };
      },
      {} as Partial<Record<EntityTypesSupportedByMapsPage, PinLegend["id"]>>
    )
);
